import { forwardRef, useState, useEffect } from "react";
import { cn } from "@/utils/utils";
import Input from "@/components/ui/input";
import AddActionButton from "@/components/ui/add-action-button";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import { Close } from "@dreamhub/designsystem/icons";
import { emailRegex } from "@/utils/validation";
import { usePersonEmailValidation } from "@/entities/people/hooks";
import { isEmpty } from "lodash";
import { getFullName } from "@/entities/people/helpers";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { IconButton } from "@/components/ui/icon-button";
import ClickableLink from "@/components/ui/clickable-link";

export interface MultiEmailFieldProps {
  value: string[] | null;
  onChange: (value: string[] | null) => void;
  name: string;
  className?: string;
  placeholder?: string;
  onValidationChange?: (hasValidationErrors: boolean) => void;
}

const MultiEmailField = forwardRef<HTMLDivElement, MultiEmailFieldProps>(
  (
    { value, onChange, name, className, placeholder, onValidationChange },
    ref
  ) => {
    const { t } = useTranslation();
    const [emails, setEmails] = useState<string[]>(() => {
      if (value && value.length > 0) {
        return [...value];
      }
      return [""];
    });
    const [currentValidationEmail, setCurrentValidationEmail] =
      useState<string>("");
    const [currentValidationIndex, setCurrentValidationIndex] =
      useState<number>(-1);
    const [validationTrigger, setValidationTrigger] = useState<number>(0);
    const [emailValidationResults, setEmailValidationResults] = useState<
      Record<number, any>
    >({});

    const emailValidation = usePersonEmailValidation(
      currentValidationEmail,
      currentValidationEmail.length > 0 && validationTrigger > 0
    );

    useEffect(() => {
      if (
        currentValidationIndex < 0 ||
        emailValidation?.isLoading ||
        emailValidation?.isError
      ) {
        return;
      }

      const person = emailValidation?.data;
      const searchEmail = currentValidationEmail.toLowerCase();
      const personEmails =
        person?.emails ?? (person?.email ? [person.email] : []);
      const isRelevantMatch =
        person &&
        searchEmail.length > 0 &&
        personEmails.some(
          (personEmail: string) =>
            personEmail.toLowerCase() === searchEmail
        );

      if (isRelevantMatch) {
        setEmailValidationResults((prev) => ({
          ...prev,
          [currentValidationIndex]: person
        }));
      } else if (searchEmail.length > 0) {
        setEmailValidationResults((prev) => {
          const newResults = { ...prev };
          delete newResults[currentValidationIndex];
          return newResults;
        });
      }
    }, [
      emailValidation?.data,
      emailValidation?.isLoading,
      emailValidation?.isError,
      currentValidationIndex,
      currentValidationEmail
    ]);

    useEffect(() => {
      if (value === null || (Array.isArray(value) && value.length === 0)) {
        setEmails([""]);
      } else if (value && Array.isArray(value)) {
        setEmails([...value]);
      }
    }, [value]);

    useEffect(() => {
      const hasErrors = Object.keys(emailValidationResults).some(
        (key) => !isEmpty(emailValidationResults[parseInt(key)])
      );
      onValidationChange?.(hasErrors);
    }, [emailValidationResults, onValidationChange]);

    const notifyChange = (newEmails: string[]) => {
      const validEmails = newEmails.filter((email) => email.trim() !== "");
      onChange(validEmails.length > 0 ? validEmails : null);
    };

    const handleEmailChange = (index: number, newValue: string) => {
      const updatedEmails = [...emails];
      updatedEmails[index] = newValue;
      setEmails(updatedEmails);
      notifyChange(updatedEmails);
      // Clear validation result for this index when email changes
      setEmailValidationResults((prev) => {
        const newResults = { ...prev };
        delete newResults[index];
        return newResults;
      });
      if (currentValidationIndex === index) {
        setCurrentValidationEmail("");
        setCurrentValidationIndex(-1);
        setValidationTrigger(0);
      }
    };

    const addEmailField = () => {
      const newEmails = [...emails, ""];
      setEmails(newEmails);
    };

    const removeEmailField = (index: number) => {
      if (emails.length > 1) {
        const updatedEmails = emails.filter((_, i) => i !== index);
        setEmails(updatedEmails);
        notifyChange(updatedEmails);

        // Clean up validation results - remove the deleted index and shift down higher indices
        setEmailValidationResults((prev) => {
          const newResults: Record<number, any> = {};
          Object.keys(prev).forEach((key) => {
            const keyIndex = parseInt(key);
            if (keyIndex < index) {
              // Keep results for indices before the removed one
              newResults[keyIndex] = prev[keyIndex];
            } else if (keyIndex > index) {
              // Shift down results for indices after the removed one
              newResults[keyIndex - 1] = prev[keyIndex];
            }
            // Skip the removed index
          });
          return newResults;
        });
      }
    };

    const isValidEmail = (email: string) => {
      if (email.trim() === "") return true;
      return emailRegex.test(email);
    };

    return (
      <div ref={ref} className={cn("space-y-3 flex flex-col", className)}>
        {emails.map((email, index) => (
          <div key={index} className={cn("flex items-center", emails.length > 1 && "gap-2")}>
            <div className="flex-1">
              <Input
                type="email"
                placeholder={placeholder}
                value={email}
                onChange={(e) => handleEmailChange(index, e.target.value)}
                onBlur={(e) => {
                  const emailValue = e.target.value.trim();
                  if (isValidEmail(e.target.value)) {
                    setCurrentValidationEmail(emailValue);
                    setCurrentValidationIndex(index);
                    // Increment trigger to force re-validation
                    setValidationTrigger((prev) => prev + 1);
                  } else {
                    // Clear validation result for this index if email becomes invalid
                    setEmailValidationResults((prev) => {
                      const newResults = { ...prev };
                      delete newResults[index];
                      return newResults;
                    });
                  }
                }}
                className={cn(
                  !isValidEmail(email) ||
                    !isEmpty(emailValidationResults[index])
                    ? "border-border-field-error hover:border-border-field-error-hover focus:border-border-field-error-focus"
                    : "border-border-field-default"
                )}
                data-test={`${name}-email-input-${index}`}
              />
              {!isValidEmail(email) && (
                <Text variant="smBodyError">{t("people.emailError")}</Text>
              )}
              {!isEmpty(emailValidationResults[index]) && (
                <div className="flex gap-1 mt-1 ml-1">
                  <Text variant="smBodyError">
                    {t("people.emailTaken", {
                      name: getFullName(emailValidationResults[index])
                    })}
                  </Text>
                  <ClickableLink
                    href={`/${entityTypeToEntityName[EntityType.PERSON]}/${emailValidationResults[index].id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Text
                      variant="smBody"
                      className="text-link hover:text-link-hover transition-colors ease-in-out duration-200"
                    >
                      {t("people.viewPersonPage")}
                    </Text>
                  </ClickableLink>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {emails.length > 1 && (
                <IconButton
                  type="button"
                  size="sm"
                  iconColor="secondary"
                  variant="iconNoBg"
                  onClick={() => removeEmailField(index)}
                  data-test={`${name}-remove-email-button-${index}`}
                >
                  <Close />
                </IconButton>
              )}
            </div>
          </div>
        ))}
        <AddActionButton
          label={t("people.addEmail")}
          type="button"
          onClick={addEmailField}
          className="h-fit self-end gap-1"
        />
      </div>
    );
  }
);

MultiEmailField.displayName = "MultiEmailField";

export default MultiEmailField;
